// Pixel page transition. On an internal link click the cursor-trail canvas is
// filled square by square from the click point, then we navigate. The next
// page boots already covered (see the inline script in html-header.twig, which
// paints the same cover before first render) and clears the squares from the
// same point.

import gsap from "gsap";
import PixelOverlay, { DURATION } from "./pixel-overlay";
import { pageReady } from "../../utils/page-ready";

const KEY = "vs-pt";
const MAX_AGE = 5000; // ms, ignore a stale flag (e.g. navigation never happened)

// Links whose click is taken over by another delegated handler (add to cart
// without leaving the page, checkout login prompt opening the auth modal).
// Those handlers also listen on document, so we can't rely on
// e.defaultPrevented: it depends on init order.
const JS_HANDLED = ".ajax_add_to_cart, .js-subscribe-lifetime, .js-checkout-btn, .wc-block-checkout__login-prompt";
const WC_ACTION_PARAMS = ["add-to-cart", "remove_item", "undo_item", "wc-ajax"];

function readFlag() {
  try {
    const f = JSON.parse(sessionStorage.getItem(KEY));
    sessionStorage.removeItem(KEY);
    if (f && Date.now() - f.ts < MAX_AGE) return f;
  } catch (e) {}
  return null;
}

function writeFlag(x, y) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ x, y, ts: Date.now() }));
  } catch (e) {}
}

// Page content fades alongside the squares. Everything in <body> but the canvas.
function content() {
  return [...document.body.children].filter(
    (el) => !el.classList.contains("cursor-trail") && !el.classList.contains("pixel-overlay") && !el.classList.contains("perf-debug") && !/^(SCRIPT|STYLE|LINK)$/.test(el.tagName)
  );
}

function fade(to) {
  return gsap.to(content(), {
    opacity: to,
    duration: DURATION / 1000,
    ease: "none",
    overwrite: true,
    onComplete: () => to === 1 && gsap.set(content(), { clearProps: "opacity" }),
  });
}

export default class PageTransition {
  static init() {
    const trail = PixelOverlay.instance;
    if (!trail) return;
    return (PageTransition.instance = new PageTransition(trail));
  }

  // Navigates with the transition from code (clickable elements that aren't
  // links). `ready` (optional promise) holds the navigation back until it
  // settles, e.g. a request that must land first; the fill runs meanwhile.
  // Falls back to a plain navigation when the transition isn't there.
  static go(href, e, el, ready) {
    const pt = PageTransition.instance;
    if (pt) pt.navigate(href, e, el, ready);
    else Promise.resolve(ready).finally(() => (location.href = href));
  }

  constructor(trail) {
    this.trail = trail;
    this.busy = false;

    document.addEventListener("click", (e) => this.onClick(e));
    // Back/forward cache restores the page as we left it (covered): reset it.
    window.addEventListener("pageshow", (e) => {
      if (!e.persisted) return;
      this.busy = false;
      this.trail.clear();
      gsap.killTweensOf(content());
      gsap.set(content(), { clearProps: "opacity" });
    });

    const flag = readFlag();
    if (flag) this.enter(flag);
  }

  // Arriving on a page after one of our transitions. Only then: a plain
  // navigation keeps the native view-transition fade (_view-transitions.scss).
  async enter({ x, y }) {
    // Entrances wait for the overlay to be (almost) gone, or they'd play behind it.
    const block = pageReady.block("page-transition");
    try {
      this.trail.cover(x, y);
      // The page is already there under the opaque squares (no fade in): the CSS
      // stand-in, which also hides the content, goes once the canvas is painted.
      // Two frames, so the freshly created canvas is composited before the stand-in goes.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => document.documentElement.classList.remove("pt-cover"))
      );
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      block.releaseIn(DURATION / 1000);
      await this.trail.reveal(x, y);
      document.documentElement.classList.remove("pt-arrive");
    } finally {
      block.release();
    }
  }

  // Returns why a click is not handled, or null when it is.
  skipReason(e, a) {
    if (e.defaultPrevented) return "defaultPrevented (another handler, e.g. a slider drag guard)";
    if (e.button !== 0) return "not the main button";
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return "modifier key";
    if (!a) return "no <a href> ancestor";
    if (a.target === "_blank" || a.hasAttribute("download")) return "target/download";
    if (a.hasAttribute("data-no-transition")) return "data-no-transition";
    if (a.matches(JS_HANDLED)) return "handled in JS (cart)";
    if (!/^https?:$/.test(a.protocol) || a.origin !== location.origin) return `other origin (${a.origin})`;
    // WooCommerce action links (?add-to-cart=, ?remove_item=, ...) are not page changes.
    // (an <a> has .search but no .searchParams: go through URL)
    const params = new URL(a.href).searchParams;
    if (WC_ACTION_PARAMS.some((p) => params.has(p))) return "woocommerce action link";
    if (a.pathname === location.pathname && a.search === location.search) {
      return a.hash ? "same-page anchor" : "same page";
    }
    if (/^\/(wp-admin|wp\/wp-admin|wp-login)/.test(a.pathname)) return "admin/login";
    return null;
  }

  onClick(e) {
    const a = e.target.closest?.("a[href]");
    if (this.busy) return;
    const reason = this.skipReason(e, a);
    if (reason) {
      // Link to the page we're already on: don't reload it. (Anchors keep their default scroll.)
      if (reason === "same page") e.preventDefault();
      if (import.meta.env.DEV && a) console.log("[page-transition] skipped:", reason, a.href);
      return;
    }

    e.preventDefault();
    this.navigate(a.href, e, a);
  }

  // Fills the overlay from the click point, then navigates to href.
  navigate(href, e, el, ready) {
    if (this.busy) return;
    this.busy = true;

    // Keyboard activation reports 0,0: fall back to the element's centre.
    let x = e?.clientX || 0;
    let y = e?.clientY || 0;
    if (!x && !y && el) {
      const r = el.getBoundingClientRect();
      x = r.left + r.width / 2;
      y = r.top + r.height / 2;
    }

    fade(0);
    // A failed `ready` still navigates (the caller decides what failure means).
    const settled = Promise.resolve(ready).catch(() => {});
    Promise.all([this.trail.fill(x, y), settled]).then(() => {
      writeFlag(x, y);
      location.href = href;
    });
  }
}
